import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { PHOTOS, TIDYCAL } from "@/lib/images";

const BOOK_URL = TIDYCAL.clarity;

export default function Hero() {
  const { t } = useT();
  const h = t.hero;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 vk-grain overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-10 items-center">
        <div className="col-span-12 lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 border vk-line-strong text-[11px] uppercase tracking-[0.22em] mb-10"
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 bg-[#E0B25B] rounded-full" />
            <span>{h.eyebrow}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif font-light text-[clamp(2.4rem,6.4vw,6rem)] leading-[0.95] tracking-[-0.02em]"
            data-testid="hero-headline"
          >
            {h.headline.l1}
            <br />
            <span className="italic font-light">{h.headline.l2}</span>
            <br />
            {h.headline.l3}
            <br />
            {h.headline.l4} <span className="text-[#E0B25B]">{h.headline.l5}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-10 max-w-xl text-lg md:text-xl text-[#6B4E6F] leading-relaxed"
            data-testid="hero-subhead"
          >
            {h.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#4B2F4F] text-[#F7F3F7] px-8 py-5 text-[12px] uppercase tracking-[0.24em] font-medium hover:bg-[#E0B25B] transition-colors duration-300"
            >
              {h.ctaPrimary}
              <ArrowRight
                size={16}
                strokeWidth={1.4}
                className="group-hover:translate-x-1 transition-transform duration-300"
              />
            </a>
            <a
              href="#sobre"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-3 px-8 py-5 text-[12px] uppercase tracking-[0.24em] font-medium border vk-line-strong hover:bg-[#4B2F4F] hover:text-[#F7F3F7] transition-colors duration-300"
            >
              {h.ctaSecondary}
              <ArrowDown
                size={16}
                strokeWidth={1.4}
                className="group-hover:translate-y-1 transition-transform duration-300"
              />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-6 text-[12px] tracking-[0.04em] text-[#6B4E6F]"
            data-testid="hero-languages"
          >
            {h.languages}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2 }}
          className="col-span-12 lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] overflow-hidden bg-[#F3D9C8]">
            <img
              src={PHOTOS.heroPortrait}
              alt="VeronikaconK"
              loading="eager"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#4B2F4F]/15 via-transparent to-transparent" />
          </div>

          <div className="hidden md:block absolute -bottom-10 -left-10 bg-[#F7F3F7] border vk-line p-6 max-w-[280px]">
            <p className="font-serif italic text-2xl leading-tight text-[#4B2F4F]">
              {h.tagline}
            </p>
            <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#E0B25B]">
              VeronikaconK
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
